import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

import { RootState } from '~/redux/store'

import { CampaignContent } from '~/shared/campaign/Campaign.types'

interface ModalState {
  campaignContent?: CampaignContent
  showFeedbackModal: boolean
  showSubscriptionActivityModal: boolean
  hasPurchaseTutoringPackages: boolean
  showAndroidAppModal: boolean
  showFreemiumModal: boolean
  showDeferredPaywall: boolean
  isProgressMigrationFlowOpen: boolean
  /** A curriculum activity is open over the level, so the level's own video should not play on behind it. */
  isCurriculumActivityOpen: boolean
  isStateSwitchModalOpen: boolean
}

const initialState: ModalState = {
  campaignContent: undefined,
  showFeedbackModal: false,
  showSubscriptionActivityModal: false,
  hasPurchaseTutoringPackages: false,
  showAndroidAppModal: false,
  showFreemiumModal: false,
  showDeferredPaywall: false,
  isProgressMigrationFlowOpen: false,
  isCurriculumActivityOpen: false,
  isStateSwitchModalOpen: false,
}

const slice = createSlice({
  name: 'modals',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      if (action.payload.modals.campaignContent) {
        return {
          ...state,
          campaignContent: action.payload.modals.campaignContent,
        }
      }
      return state
    })
  },
  reducers: {
    setCampaignModalContent: (state, action: PayloadAction<{ campaignContent: CampaignContent }>) => {
      return {
        ...state,
        campaignContent: action.payload.campaignContent,
      }
    },
    setShowFeedbackModal: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showFeedbackModal: action.payload,
      }
    },
    setShowSubscriptionActivityModal: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showSubscriptionActivityModal: action.payload,
      }
    },
    setIsCurriculumActivityOpen: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isCurriculumActivityOpen: action.payload,
      }
    },
    setShowSubscriptionStaticModal: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showSubscriptionStaticModal: action.payload,
      }
    },
    setHasPurchaseTutoringPackages: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasPurchaseTutoringPackages: action.payload,
      }
    },
    setShowAndroidAppModal: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showAndroidAppModal: action.payload,
      }
    },

    setShowFreemiumModal: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showFreemiumModal: action.payload,
      }
    },
    setShowDeferredPaywall: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        showDeferredPaywall: action.payload,
      }
    },
    setProgressMigrationFlowOpen: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isProgressMigrationFlowOpen: action.payload,
      }
    },
    setStateSwitchModalOpen: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isStateSwitchModalOpen: action.payload,
      }
    },
  },
})

export const selectIsCampaignModalContent = (state: RootState): CampaignContent | undefined => {
  return state.modals.campaignContent
}

export const selectShowFeedbackModal = (state: RootState): boolean => {
  return state.modals.showFeedbackModal
}

export const selectHasPurchaseTutoringPackages = (state: RootState): boolean => {
  return state.modals.hasPurchaseTutoringPackages
}

export const selectShowSubscriptionActivityModal = (state: RootState): boolean => {
  return state.modals.showSubscriptionActivityModal
}

export const selectShowAndroidAppModal = (state: RootState): boolean => {
  return state.modals.showAndroidAppModal
}

export const selectShowFreemiumModal = (state: RootState): boolean => {
  return state.modals.showFreemiumModal
}

export const selectShowDeferredPaywall = (state: RootState): boolean => {
  return state.modals.showDeferredPaywall
}

export const selectIsCurriculumActivityOpen = (state: RootState): boolean => {
  return state.modals.isCurriculumActivityOpen
}

export const selectIsProgressMigrationFlowOpen = (state: RootState): boolean => {
  return state.modals.isProgressMigrationFlowOpen
}

export const selectIsStateSwitchModalOpen = (state: RootState): boolean => {
  return state.modals.isStateSwitchModalOpen
}

export const {
  setCampaignModalContent: setCampaignModalContentCreator,
  setShowFeedbackModal: setShowFeedbackModalCreator,
  setShowSubscriptionActivityModal: setShowSubscriptionActivityModalCreator,
  setShowSubscriptionStaticModal: setShowSubscriptionStaticModalCreator,
  setHasPurchaseTutoringPackages: setHasPurchaseTutoringPackagesCreator,
  setShowAndroidAppModal: setShowAndroidAppModalCreator,
  setShowFreemiumModal: setShowFreemiumModalCreator,
  setShowDeferredPaywall: setShowDeferredPaywallCreator,
  setProgressMigrationFlowOpen,
  setIsCurriculumActivityOpen: setIsCurriculumActivityOpenCreator,
  setStateSwitchModalOpen,
} = slice.actions

export default slice.reducer
